/* eslint-disable @typescript-eslint/no-use-before-define */
import axios from "axios";

import { isDemo, isIOS } from "@/utils";
import { QrCodeObject } from "@/models/QrCodeObject";
import { Shortlink } from "@/models/Shortlink";
import Project from "@/models/Project";
import { convertCamelToSnake, getApiEndpoints, getUserOS } from "@/utils/utils";

let instance: QrCodeService = null;
export enum TrackingEvents {
    OPEN = "open",
    BURN = "burn"
}

interface StatsPayload {
    dt: number;
    ms: number;
    timezone: number;
    sessionId: string;
    userId: string;
    projectId: number;
    shortlinkId: number;
    fp: string;
    object: string;
    event: TrackingEvents;
    osName: string;
    organizationId?: string;
    data?: any;
    country?: string;
    city?: string
}

export class QrCodeService {
    cityName = "";
    countryName = "";
    async loadQrCode(qrId: string): Promise<QrCodeObject> {
        let qrcodeObject;

        /**
         * In STQR manager, the app is included in an iframe.
         * In this case, we ask for the QRCode to the parent window using postMessage.
         */
        if (isDemo() && window.parent.location !== window.location && window.parent.postMessage) {
            qrcodeObject = await new Promise(resolve => {
                const onMessage = message => {
                    try {
                        const json = JSON.parse(message.data);

                        if (json?.type === "qrcodeObject" && json?.value) {
                            window.removeEventListener("message", onMessage);
                            resolve(json.value);
                        }
                    } catch {
                        return;
                    }
                };
                window.addEventListener("message", onMessage);
                window.parent.postMessage("getQrCode", "*");
            });
        } else {
            /**
             * Otherwise, simply ask to the API
             */
            const response = await axios.get(`${getApiEndpoints().SHORTLINKS}/${qrId}/with-related-data`);
            qrcodeObject = response.data;
        }
        if (window.stqr === undefined) {
            window.stqr = {};
        }
        window.stqr.qrcode = qrcodeObject;
        const event = new CustomEvent("qrcode", {
            detail: qrcodeObject
        });
        window.dispatchEvent(event);

        return qrcodeObject;
    }

    async logScan(fp: string, project: Project, shortlink: Shortlink, action: TrackingEvents, burnId?: string) {
        if (!project) {
            throw new Error("Project is null");
        }
        if (!shortlink) {
            throw new Error("Shortlink is null");
        }
        const dts = Math.floor(Date.now() / 1000);
        const dtms = Date.now();
        const offsetMinutes = new Date().getTimezoneOffset(); // Example: -300 (for UTC-5)
        const timezoneOffset = offsetMinutes / -60; // Convert to hours
        const uniqueTmp = Date.now() - Math.floor(Math.random() * 10000);
        try {
            const reqData: StatsPayload = {
                dt: dts,
                ms: dtms,
                timezone: timezoneOffset,
                sessionId: uniqueTmp.toString(),
                userId: project.userId.toString(),
                projectId: project.id,
                shortlinkId: shortlink.id,
                fp: fp.toString(),
                object: "shortlink",
                event: action,
                osName: getUserOS(),
                country: this.countryName || "Unknown",
                city: this.cityName || "Unknown",
                organizationId: project.organizationId.toString()
            };
            if (burnId) {
                reqData.data = burnId;
            }
            const data = `stqr.tracks;${uniqueTmp};${JSON.stringify(convertCamelToSnake(reqData))}`;
            const url = getApiEndpoints().STATS;
            const response = await fetch(url, {
                method: "POST",
                body: data
            });

            if (response.status === 200) {
                return response;
            } else {
                throw new Error(`Error posting to ${url}, status: ${response.status}`);
            }
        } catch (error) {
            console.error(error);
            throw new Error("Error logging scan");
        }
    }

    public async getGeo(){
        const geo = await axios.get(getApiEndpoints().GEO_IP);
        this.cityName = geo.data?.City?.Names?.['en'];
        this.countryName = geo.data?.Country?.Names?.['en'];
        return {cityName:this.cityName, countryName: this.countryName};
    }

    public async computeVideoUrl(qrcodeObject: QrCodeObject): Promise<string> {
        // if content banned, simply return empty string
        if (!qrcodeObject.project.isActive) {
            // TO DO: check if project is banned
            return "";
        }
        if (qrcodeObject.currentVideoUrl?.length > 0) {
            // otherwise use from defaultVideoUrl field if available
            return qrcodeObject.currentVideoUrl;
        } else {
            return qrcodeObject.project.defaultVideoUrl;
        }
    }
    public async computeAudioUrl(qrcodeObject: QrCodeObject): Promise<string> {
        // if content banned, simply return empty string
        if (!qrcodeObject.project.isActive) {
            // TO DO: check if project is banned
            return "";
        }
        if (qrcodeObject.currentAudioUrl?.length > 0) {
            // otherwise use from defaultVideoUrl field if available
            return qrcodeObject.currentAudioUrl;
        } else {
            return "";
        }
    }

    public computeCustomHtmlContent(
        qrcodeObject: QrCodeObject
    ): { html: string; fullHtml: string; scripts: string[] } {
        const result = {
            html: "",
            fullHtml: "",
            scripts: []
        };
        if (
            (qrcodeObject.template.landingHtml?.length > 0 &&
                qrcodeObject.template.landingHtml[0] === "<") ||
            qrcodeObject.template.landingFullHtml?.length > 0
        ) {
            // extract javascript if necessary
            const jsRegex = RegExp(/<script.*?>([\n\s\w\W\d\D]+?)<\/script>/, "i");
            const remoteJsRegex = RegExp(/<script.*?src=["'](.+?)["'].*?><\/script>/, "i");

            let htmlContent = qrcodeObject.template.landingHtml;
            let htmlFullContent = qrcodeObject.template.landingFullHtml;
            let match;
            if (!qrcodeObject.project.isUsingLandingFullHtml) {
                while ((match = remoteJsRegex.exec(htmlContent)) !== null) {
                    htmlContent =
                        htmlContent.substr(0, match.index - 1) +
                        htmlContent.substr(match.index + match[0].length);
                    result.scripts.push(match[1]);
                }
                while ((match = jsRegex.exec(htmlContent)) !== null) {
                    htmlContent =
                        htmlContent.substr(0, match.index - 1) +
                        htmlContent.substr(match.index + match[0].length);
                    result.scripts.push(match[1]);
                }
            } else {
                while ((match = remoteJsRegex.exec(htmlFullContent)) !== null) {
                    htmlFullContent =
                        htmlFullContent.substr(0, match.index - 1) +
                        htmlFullContent.substr(match.index + match[0].length);
                    result.scripts.push(match[1]);
                }
                while ((match = jsRegex.exec(htmlFullContent)) !== null) {
                    htmlFullContent =
                        htmlFullContent.substr(0, match.index - 1) +
                        htmlFullContent.substr(match.index + match[0].length);
                    result.scripts.push(match[1]);
                }
            }

            result.html = htmlContent;
            result.fullHtml = htmlFullContent;
        }

        return result;
    }

    async getWebArUrl(id: number, videoUrl: string){
        try{
            const fullUrl = this.getFullVideoPath(videoUrl)
            const res = await axios.get(`${getApiEndpoints().WEBAR_URL}/${id}/webar-url?shortlink_video_url=${fullUrl}`);
            const data = res.data;
            return data.webarUrl;
        }catch{
            console.warn('error: no ar enabled')
        }
    }

    getFullVideoPath(urlEnd: string): string {
        let fullPath: string;
        if (urlEnd.startsWith("http")) {
            fullPath = urlEnd;
        } else {
            fullPath = getApiEndpoints().API_BASE + urlEnd;
        }
        return fullPath;
    }

    async presignFile(video: string) {
        if (isDemo()) {
            return null;
        }
        const formData = new FormData();
        formData.append("files", video);
        return axios.post(getApiEndpoints().UPLOADS, formData);
    }

    async saveExperience(id: number, videoUrl: string, videoDuration: number, burnFields: string, isBurnt:boolean, isAuthRequired: boolean) {
        if (isDemo()) {
            return null;
        }
        const formData = new FormData();
        let headers = {};
        // set token if available
        if (isAuthRequired && localStorage.getItem("stqr_token")) {
           headers = { Authorization: "Bearer " + localStorage.getItem("stqr_token") };
        }
        const guid= localStorage.getItem('guest_uid'); // guest id
        const uid= localStorage.getItem('stqr_uid'); //user id
        if(guid){
            formData.append('guest_id',guid)
        }
        if(uid){
            formData.append('user_id',uid)
        }
        formData.append("shortlink_public_crypto_id", id.toString());
        formData.append("video_url", videoUrl);
        formData.append("video_duration", videoDuration.toString());
        formData.append("field_values", burnFields);
        if (isBurnt) {
            return axios.put(`${getApiEndpoints().SHORTLINK_BURNS}/${id}`, formData, { headers });
        } else {
            return axios.post(getApiEndpoints().SHORTLINK_BURNS, formData, { headers });
        }
    }
    async sendToS3(file: File, url: string, type: string) {
        if (isDemo()) {
            return null;
        }
        return await axios.put(url, file, {
            headers: { "Content-Type": type }
        });
    }
}

export default function getInstance(): QrCodeService {
    if (!instance) {
        instance = new QrCodeService();
    }

    return instance;
}
